import React from "react";

import useRemoveAttachment from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveAttachment";
import { Attachment } from "@fartherfinance/frontend/api/Tasks/Types";
import { AttachmentId, TaskId } from "@fartherfinance/frontend/api/Types";

import { useTaskPermissions } from "../../../taskPermissionsContext";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import TaskAttachment from "./TaskAttachment";

import styles from "./TaskAttachments.module.css";

interface TaskAttachmentsProps {
  taskId: TaskId;
  attachments: Attachment[];
}

const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  taskId,
  attachments,
}) => {
  const { attachmentPermissions } = useTaskPermissions();

  const auth = useAdvisorRequestAuth();
  const callRemoveAttachment = useRemoveAttachment(auth);
  const statusNotification = useStatusNotification();

  const handleCreateMenuOptions = (
    taskId: TaskId,
    attachment: Attachment
  ): MenuOption[] => {
    const downloadOption: MenuOption = {
      label: "Download",
      onClick: () => (window.location.href = attachment.downloadUrl),
    };
    const deleteOption: MenuOption = {
      label: "Delete",
      className: styles.delete,
      onClick: () => handleDeleteAttachment(taskId, attachment.id),
    };

    return [
      downloadOption,
      ...(attachmentPermissions(attachment).isAllowedToDelete
        ? [deleteOption]
        : []),
    ];
  };

  const handleDeleteAttachment = async (
    taskId: TaskId,
    attachmentId: AttachmentId
  ): Promise<void> => {
    try {
      await callRemoveAttachment({ taskId, attachmentId });

      trackEvent({ name: "Advisor Tasks Attachment Delete" });

      statusNotification("Attachment removed successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete attachment."),
        "Error"
      );
    }
  };

  return (
    <div className={styles.container}>
      {attachments.map((attachment) => (
        <TaskAttachment
          key={attachment.id}
          previewUrl={attachment.previewUrl}
          filename={attachment.filename}
          menuOptions={handleCreateMenuOptions(taskId, attachment)}
        />
      ))}
    </div>
  );
};

export default TaskAttachments;
